import type { BrandSpecial } from '@iheartjane/data-access';
import { EXTRACT, FLOWERS } from '@iheartjane/menu';
import type { PriceId, SummarizedBrandSpecial } from '@iheartjane/types';

/**
 * Minimal structural menu-product type read by `getMenuPdpApplicableGoldTagLabel`.
 * It only needs the product `kind` and the per-weight price fields.
 */
export type GoldTagMenuProduct = {
  kind: string;
} & Partial<
  Record<
    `price_${PriceId}` | `discounted_price_${PriceId}`,
    number | string | null
  >
>;

const defaultLabel = 'Cash back';

/**
 *
 * getApplicableGoldTagLabel & getPdpApplicableGoldTagLabel
 *
 * These functions determine the appropriate gold tag label to display for products with Jane Gold rewards. They analyze brand specials and return the correct label based on special types, applicable weights, and business rules.
 *
 * The business rules are:
 *
 * When a single brand special applies:
 * - Is it a product special? Use the discount label
 * - Is it a bundle special? Use 'Cash back'
 *
 * When multiple brand specials apply:
 * - Are they multiple product specials? Use the best discount label
 * - Are they multiple bundle specials? Use 'Cash back'
 * - Is it a mix of product and bundle specials? Use the best product special discount label
 */

/**
 * @param params.brandSpecials - Array of brand specials to analyze
 * @param params.applicableBrandSpecialIds - Optional array of brand special IDs that apply, sorted by best discount
 * @param params.priceId - Optional price ID to check against applicable weights
 * @returns The appropriate gold tag label string, or null if no applicable specials found
 */
export const getApplicableGoldTagLabel = ({
  brandSpecials,
  applicableBrandSpecialIds,
  priceId,
}: {
  applicableBrandSpecialIds?: number[] | readonly number[] | null;
  brandSpecials: BrandSpecial[];
  priceId?: PriceId;
}): string | null => {
  if (!applicableBrandSpecialIds) return null;

  // maintain the sort order of the brand special ids as they are sorted by the lowest price/best discount
  const applicableBrandSpecials = applicableBrandSpecialIds
    ? (applicableBrandSpecialIds
        .map((id) => brandSpecials.find((special) => special.id === id))
        .filter(Boolean) as BrandSpecial[])
    : [];

  if (applicableBrandSpecials.length > 1) {
    // if more than one, check if a product special exists
    const productBrandSpecial = applicableBrandSpecials.find(
      ({ special_type }) => special_type === 'product'
    );

    if (productBrandSpecial) {
      // return the label for the best discount
      return productBrandSpecial.discount_label;
    } else {
      // or if they're bundle specials, return default
      return defaultLabel;
    }
  }

  if (applicableBrandSpecials.length === 1) {
    const brandSpecial = applicableBrandSpecials[0];

    if (priceId) {
      const applicableWeights = brandSpecial.applicable_weights;
      // if its an array, its a product special
      if (Array.isArray(applicableWeights)) {
        return applicableWeights.includes(priceId)
          ? brandSpecial.discount_label
          : null;
      }
      // if qualify/discount, its a bundle special
      return applicableWeights?.discount?.includes(priceId) ||
        applicableWeights?.qualify?.includes(priceId)
        ? defaultLabel
        : null;
    }

    return brandSpecial.special_type === 'product'
      ? brandSpecial.discount_label
      : defaultLabel;
  }

  return null;
};

/**
 * Determines the appropriate gold tag label for PDP (Product Detail Page) using summarized brand specials.
 *
 * Provides the same functionality as `getApplicableGoldTagLabel` but uses `SummarizedBrandSpecial` type instead of `BrandSpecial`. Follows the same business rules for determining labels. It does not require the applicable brand special ids as the specials are already filtered for the user and sorted based on the lowest discounted price.
 * @param params.summarizedBrandSpecials - Array of summarized brand specials to analyze
 * @param params.priceId - Optional price ID to check against the brand special prices
 * @returns The appropriate gold tag label string, or null if no applicable specials found
 */
export const getPdpApplicableGoldTagLabel = ({
  priceId,
  summarizedBrandSpecials,
}: {
  priceId?: PriceId;
  summarizedBrandSpecials: SummarizedBrandSpecial[];
}) => {
  if (!summarizedBrandSpecials) return null;

  if (summarizedBrandSpecials.length > 1) {
    // if more than one, check if a product special exists
    const productBrandSpecial = summarizedBrandSpecials.find(
      ({ special_type }) => special_type === 'product'
    );

    if (productBrandSpecial) {
      // return the label for the best discount
      return productBrandSpecial.discount_label ?? defaultLabel;
    } else {
      // or if they're bundle specials, return default
      return defaultLabel;
    }
  }

  if (summarizedBrandSpecials.length === 1) {
    const brandSpecial = summarizedBrandSpecials[0];
    const isBundleType = brandSpecial.special_type !== 'product';

    if (priceId) {
      const brandSpecialPriceIds = brandSpecial?.prices?.map(
        (price) => price?.price_id
      );

      // if price id is in the weights, return the label
      if (brandSpecialPriceIds) {
        const labelForPriceId = brandSpecialPriceIds.includes(priceId)
          ? isBundleType
            ? defaultLabel
            : (brandSpecial.discount_label ?? defaultLabel)
          : null;

        return labelForPriceId;
      }
    }

    return brandSpecial.special_type === 'product'
      ? (brandSpecial.discount_label ?? defaultLabel)
      : defaultLabel;
  }

  return null;
};

/**
 * Builds the list of enabled (priced) weights for a menu product, used to detect
 * weight rules on brand specials.
 */
const enabledWeightsForMenuProduct = (menuProduct: GoldTagMenuProduct) => {
  const weights = ['flower', 'extract', 'vape'].includes(menuProduct.kind)
    ? menuProduct.kind === 'flower'
      ? FLOWERS
      : EXTRACT
    : [];

  return weights.reduce<
    {
      disabled: boolean;
      price?: number | string | null;
      value: PriceId;
    }[]
  >((accum, weight) => {
    const price =
      menuProduct[`discounted_price_${weight.id}`] ||
      menuProduct[`price_${weight.id}`];

    if (price) {
      accum.push({
        disabled: false,
        price,
        value: weight.id,
      });
    }

    return accum;
  }, []);
};

/**
 * Determines the appropriate top level gold tag label for Menu PDP (Product Detail Page).
 *
 * Provides similar functionality as `getApplicableGoldTagLabel`, but in the case of one applicable special, it considers if the special has weight rules. In this case, the function returns null because tags will be shown next to specific weights instead of under the product name.
 *
 * The business rules are:
 *
 * When a single brand special applies:
 * - Is it a product special? Use the discount label, but do not return a label if the special has weight rules
 * - Is it a bundle special? Use 'Cash back', but do not return a label if the special has weight rules (unless the selected weight is discounted)
 *
 * When multiple brand specials apply:
 * - Are they multiple product specials? Use the best discount label
 * - Are they multiple bundle specials? Use 'Cash back'
 * - Is it a mix of product and bundle specials? Use the best product special discount label
 * @param params.brandSpecials - Array of brand specials to analyze (sorted by best special)
 * @param params.menuProduct - The menu product to analyze
 * @param params.selectedWeight - The weight to check against applicable weights
 * @returns The appropriate gold tag label string, or null if no applicable specials found
 */
export const getMenuPdpApplicableGoldTagLabel = ({
  brandSpecials,
  menuProduct,
  selectedWeight,
}: {
  brandSpecials: BrandSpecial[];
  menuProduct: GoldTagMenuProduct;
  selectedWeight: PriceId;
}): string | null => {
  if (!brandSpecials) return null;

  // if more than one, check if a product special exists
  if (brandSpecials?.length > 1) {
    const productBrandSpecial = brandSpecials.find(
      ({ special_type }) => special_type === 'product'
    );

    if (productBrandSpecial) {
      // return the label for the best discount
      return productBrandSpecial.discount_label;
    } else {
      // or if they're bundle specials, return default
      return defaultLabel;
    }
  }

  // if a single brand special applies, check if there is a weight rule
  if (brandSpecials?.length === 1) {
    const menuProductWeights = enabledWeightsForMenuProduct(menuProduct);
    const applicableWeights = brandSpecials[0].applicable_weights;

    // if its an array, it's a product special
    if (Array.isArray(applicableWeights)) {
      const hasWeightRule =
        menuProductWeights?.find(
          (weight) => !applicableWeights.includes(weight.value)
        ) || false;

      return hasWeightRule ? null : brandSpecials[0].discount_label;
    }
    // if qualify/discount, it's a bundle special
    const hasWeightRule =
      menuProductWeights?.find(
        (weight) =>
          !applicableWeights?.discount?.includes(weight.value) &&
          !applicableWeights?.qualify?.includes(weight.value)
      ) || false;
    const isSelectedWeightDiscounted =
      applicableWeights?.discount?.includes(selectedWeight) ||
      applicableWeights?.qualify?.includes(selectedWeight);

    return hasWeightRule && !isSelectedWeightDiscounted ? null : defaultLabel;
  }

  return null;
};
